import type { Message, ReplyBotInfo } from '../../api/types'
import type { LocalChannel } from '../../db/db'
import { clipForwardProblem } from '../../lib/media'
import { canPinIn, canPostIn, canReplyPrivately, replyBotOf } from '../../lib/posting'
import type { TimelineMessage } from '../../sync/outboxMerge'

/** What I may do with a message: one set of rules for the icon row, the message sheet and swipe to reply. */
export interface MessageRules {
  /** Replying follows the posting rules (restricted channels, bot conversations, archive). */
  reply: boolean
  /** A restricted channel's reply bot, for members who cannot post there. */
  replyBot: ReplyBotInfo | null
  /** Reactions: any member of a conversation that is not archived. */
  react: boolean
  /**
   * Forwarding: any message in a conversation you belong to, archived or not; not an admin's view of a channel they're
   * not in, and not one whose clips aren't converted, which the server refuses whoever asks.
   */
  forward: boolean
  copy: boolean
  /** Your own messages, while you can still post (see canEditMessage). */
  edit: boolean
  /** Pinning follows the posting rules too. */
  pin: boolean
  pinned: boolean
  /** Your own messages, or anyone's for moderators. */
  delete: boolean
  /** What swiping the message to the right does; null: it does not move. */
  swipe: 'reply' | 'reply-privately' | null
}

/**
 * Your own messages, while you can still post; not forwarded copies, whose words are the original author's, nor answers to a bot's
 * question, whose words are the option you tapped.
 */
export function canEditMessage(message: Message, channel: LocalChannel, meId: string): boolean {
  return !message.deletedAt && message.authorId === meId && !message.forwarded && !message.answer && canPostIn(channel)
}

/**
 * What ↑ in an empty message box edits (spec §3.7): your latest message that you can still edit. While your newest
 * message is still sending, or wasn't sent, that is what it says instead: ↑ doesn't reach past it to an older one.
 */
export function lastEditableMessage(timeline: readonly TimelineMessage[], channel: LocalChannel, meId: string): Message | 'sending' | 'failed' | null {
  for (let i = timeline.length - 1; i >= 0; i--) {
    const { message, pending } = timeline[i]
    // Messages still in the outbox are yours, and come after every sent one.
    if (pending) return pending.state === 'failed' ? 'failed' : 'sending'
    if (canEditMessage(message, channel, meId)) return message
  }
  return null
}

export function messageRules(message: Message, channel: LocalChannel, meId: string, canModerate: boolean): MessageRules {
  const live = !message.deletedAt
  const mine = message.authorId === meId
  const canPost = canPostIn(channel)
  const reply = live && canPost
  const replyBot = live && canReplyPrivately(channel) ? replyBotOf(channel) : null
  return {
    reply,
    replyBot,
    react: live && channel.isMember && !channel.archivedAt,
    forward: live && channel.isMember && clipForwardProblem(message.attachments) === null,
    copy: live && Boolean(message.body),
    edit: canEditMessage(message, channel, meId),
    pin: live && canPinIn(channel),
    pinned: channel.pinnedMessageIds.includes(message.id),
    delete: live && (mine || canModerate),
    swipe: reply ? 'reply' : replyBot ? 'reply-privately' : null,
  }
}
